import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
    ActionButton,
    Button,
    DatePicker,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import ReactDOM from "react-dom";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { FormatOptionLabelMeta, SelectInstance } from "react-select";
import {
    IAddInsurancePayment,
    IAddPaymentResponse,
    PaymentMethod,
    PaymentType,
    RefundReason,
    WriteOffCodes,
    WriteOffCodesDecription,
    WriteOffSource,
} from "../../../billing/types/billing.types";
import { APP_COLORS } from "../../../../utils/constants";
import { useAddInvoicePayment } from "../../../../hooks/mutations/billing";
import {
    IClaimAdjustments,
    IInsurancePayments,
    IRemit,
} from "../../types/remits.types";
import {
    formatDate,
    handleFormatDatePickerValue,
    handleRemoveEmptyParamFromQueryParams,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import { formatRemarkOptionLabel } from "../../../billing/helpers/billing.helpers";
import useToast from "../../../../hooks/useToast";
import { IPaginatedApiResponse } from "../../../../utils/types/api-response";
import MinusCircleIcon from "../../../../components/Icons/MinusCircle";
import { useFetchRemitClaims } from "../../hooks/remits.queries";
import { useGetRemarkCodes } from "../../../remark-codes/hooks/remark-code.queries";
import { ClaimStatus } from "../../../claims/types/claims.types";
import { useUpdateClaimStatus } from "../../../claims/hooks/claims.mutations";
import CheckIcon from "../../../../icons/Check";
import AlertTraingleIcon from "../../../../icons/AlertTraingle";
import {
    getAdjustmentCodeTotals,
    getPaymentRemarkCodeTotals,
} from "../../helpers/helpers.remits";

interface AddRemitInsurancePaymentFormProps {
    invoiceId: string;
    onCancelClick: () => void;
    onFormSubmit: () => void;
    /**
     * When provided, the remit dropdown is sourced from this list (with
     * CPT-prominent labels) instead of fetching all remits for the note.
     * Used by the batch view so the dropdown is scoped to remits in the
     * batch and CPT codes can be picked by the user.
     */
    availableRemits?: IRemit[];
    remitClaimAdjustments?: IClaimAdjustments[];
    insurancePayments?: IInsurancePayments[];
}

type Options = {
    label: React.ReactNode;
    value: string | undefined;
};

export default function AddRemitInsurancePaymentForm({
    invoiceId,
    onCancelClick,
    onFormSubmit,
    availableRemits,
    remitClaimAdjustments,
    insurancePayments,
}: AddRemitInsurancePaymentFormProps) {
    const [paymentDate, setPaymentDate] = React.useState<Date | null>(
        new Date()
    );
    const [paymentSource, setPaymentSource] =
        React.useState<string>("insurance");
    const codeSelectRefs = React.useRef<(SelectInstance | null)[]>([]);

    const [searchParams] = useSearchParams();

    const { claimId, noteId, clientId } = useParams();

    const remarkCodes = useGetRemarkCodes();

    const remitId = availableRemits ? null : searchParams.get("remit_in_view");

    const addPayment = useAddInvoicePayment(invoiceId);

    const changeClaimStatus = useUpdateClaimStatus();

    const {
        data: remitData,
        isLoading,
        error,
    } = useFetchRemitClaims(
        {
            note_id: noteId,
        },
        Boolean(noteId) && !remitId
    );

    const remitsForDropdown = availableRemits || remitData?.data;

    const getRemitOptionLabel = (remit: IRemit) => {
        const cpt = remit.remittance_cpt_code || remit.cpt_code;
        if (availableRemits) {
            return cpt ? `CPT ${cpt}` : "Unspecified CPT";
        }
        return `DOS: ${formatDate(remit.date_of_service)} / CN: ${
            remit.claim_number || "--"
        } / PD: ${formatDate(remit.remit_payment_date)}`;
    };

    const claimRemitsForSelect =
        remitsForDropdown?.map((remit) => ({
            value: remit.remittance_id,
            label: getRemitOptionLabel(remit),
        })) || [];

    const {
        register,
        control,
        watch,
        handleSubmit,
        setValue,
        formState: { errors, dirtyFields },
    } = useForm<IAddInsurancePayment>({
        mode: "onChange",
        resolver: yupResolver(
            yup.object().shape({
                remark_codes_payment_form: yup.array().of(
                    yup.object().shape({
                        amount: yup
                            .mixed()
                            .test(
                                "is-amount-valid",
                                "Enter valid amount",
                                (value) => {
                                    if (
                                        value !== undefined &&
                                        value !== null &&
                                        value !== ""
                                    ) {
                                        return yup
                                            .number()
                                            .required("Enter valid amount")
                                            .isValidSync(value);
                                    }

                                    return true;
                                }
                            ),
                        code: yup
                            .string()
                            .when("amount", (amount: string, field) =>
                                amount
                                    ? field.required("Code is required")
                                    : field
                            ),
                    })
                ),
                payment_amount: yup
                    .number()
                    .required("Please enter a valid amount"),

                payment_date: yup.date().required("Payment date is required"),

                payment_method: yup
                    .string()
                    .when("payment_type", (paymentType: string, field) =>
                        paymentType === "credit"
                            ? field.required("Payment method is required")
                            : field
                    ),
            })
        ),
        defaultValues: {
            payment_type: "credit",
            payment_date: handleFormatDatePickerValue(new Date()),
            remark_codes_payment_form: [{ code: "", amount: "" }],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "remark_codes_payment_form",
    });

    const paymentType = watch("payment_type");
    const paymentMethod = watch("payment_method");

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const remitIdForForm =
        remitId ||
        (remitsForDropdown && remitsForDropdown.length > 1
            ? watch("remittance_id")
            : remitsForDropdown?.[0].remittance_id);

    const claimIdForForm =
        claimId ||
        (remitsForDropdown && remitsForDropdown.length > 1
            ? remitsForDropdown.find(
                  (item) => item.remittance_id === watch("remittance_id")
              )?.claim_id
            : remitsForDropdown?.[0].claim_id);

    const remarkPairs = watch("remark_codes_payment_form") || [];

    const selectedRemit = remitsForDropdown?.find(
        (remit) => remit.remittance_id === remitIdForForm
    );
    const adjustmentCodeTotals = getAdjustmentCodeTotals(
        remitClaimAdjustments ?? selectedRemit?.claim_adjustments ?? []
    );
    const existingRemarkCodeTotals = getPaymentRemarkCodeTotals(
        insurancePayments ?? selectedRemit?.insurance_payments ?? []
    );
    const enteredRemarkCodeTotals = new Map<string, number>();
    remarkPairs.forEach((item) => {
        if (!item.code) return;

        const code = item.code.toLowerCase();
        enteredRemarkCodeTotals.set(
            code,
            (enteredRemarkCodeTotals.get(code) ?? 0) + Number(item.amount ?? 0)
        );
    });

    // Flag an entered remark amount that differs from the remit's adjustment
    const remarkAmountWarning = (index: number) => {
        const pair = remarkPairs[index];
        if (!pair?.code || !pair?.amount) return null;

        const code = pair.code.toLowerCase();
        const expected = adjustmentCodeTotals.get(code);
        if (expected == null) return null;

        const total =
            (existingRemarkCodeTotals.get(code) ?? 0) +
            (enteredRemarkCodeTotals.get(code) ?? 0);

        return Math.round(total * 100) !== Math.round(expected * 100)
            ? `${pair.code.toUpperCase()} total should be ${showMoneyInAppFormat(
                  expected
              )}`
            : null;
    };

    const paymentTypeForSelect: Options[] = Object.values(PaymentType).map(
        (item) => ({
            value: item,
            label:
                item === "refund"
                    ? "Recoupment"
                    : makeStringFirstLetterCapital(removeEnumUnderscore(item)),
        })
    );

    const remarkCodeForSelect: Options[] = remarkCodes.data
        ? remarkCodes.data?.data
              .map((item) => ({
                  value: item.code,
                  label: (
                      <span className="capitalize">{`${item.code}: ${item.description}`}</span>
                  ),
              }))
              .sort((a, b) => a.value.localeCompare(b.value))
        : [];

    const writeOffCodesForSelect: Options[] = Object.values(WriteOffCodes).map(
        (item) => ({
            value: item,
            label: WriteOffCodesDecription[item],
        })
    );

    const getWriteOffCodeDescription = (code: string) => {
        const writeOffDescriptions = Object.values(WriteOffCodesDecription);
        const matchingDescription = writeOffDescriptions?.find(
            (description) =>
                code?.toLowerCase() === description.split(":")[0]?.toLowerCase()
        );
        return matchingDescription || "--";
    };

    const refundReasonForSelect: Options[] = [
        ...Object.values(RefundReason).map((item) => ({
            value: item,
            label: (
                <>{makeStringFirstLetterCapital(removeEnumUnderscore(item))} </>
            ),
        })),
    ];

    const paymentMethodForSelect: Options[] = Object.values(PaymentMethod).map(
        (item) => ({
            value: item,
            label:
                item === "cheque"
                    ? "Check"
                    : makeStringFirstLetterCapital(removeEnumUnderscore(item)),
        })
    );

    const paymentSourcesForSelect: Options[] = Object.values(WriteOffSource)
        .filter(
            (item) =>
                item !== WriteOffSource.CO_INSURANCE &&
                item !== WriteOffSource.PR100
        )
        .map((item) => ({
            value: item,
            label: makeStringFirstLetterCapital(removeEnumUnderscore(item)),
        }));

    const getPaymentType = () => {
        let type;
        if (paymentSource === "insurance") {
            switch (paymentType) {
                case "write_off":
                    type = "wbi";
                    break;
                case "refund":
                    type = "ri";
                    break;
                default:
                    type = "i";
            }
        } else if (paymentSource === "interest_payment") {
            switch (paymentType) {
                case "refund":
                    type = "rip";
                    break;
                default:
                    type = "ip";
            }
        }

        return type;
    };

    const handleFormSubmit = (wbAction?: "close" | "hold") => {
        return (data: IAddInsurancePayment) => {
            const payload = {
                ...data,
                payment_amount: parseFloat(data?.payment_amount),
                payment_method: data.payment_method,
                payment_date: handleFormatDatePickerValue(data.payment_date),
                payment_type: getPaymentType(),
                claim_id: claimIdForForm,
                remittance_id: remitIdForForm,
                remark_codes: data.remark_codes || undefined,
                close_invoice: wbAction === "close",
            };

            if (dirtyFields.remark_codes_payment_form) {
                const getPairs = data.remark_codes_payment_form?.map(
                    (pair) => ({
                        code: pair.code,
                        amount: pair.amount ? parseFloat(pair.amount) : null,
                    })
                );

                payload.remark_codes_payment =
                    getPairs && getPairs?.length > 0
                        ? getPairs?.reduce((acc, curr) => {
                              // eslint-disable-next-line no-param-reassign
                              if (curr.code) acc[curr.code] = curr.amount;
                              return acc;
                          }, {} as Record<string, number | null>)
                        : undefined;
            }

            // clear remark_codes_payment_form used in form
            payload.remark_codes_payment_form = undefined;

            const closeClaim = claimIdForForm && wbAction === "close";

            addPayment.mutate(payload, {
                onSuccess: (response) => {
                    if (closeClaim) {
                        const changeStatusPayload = {
                            claim_id: claimIdForForm,
                            claim_status: ClaimStatus.CLOSED,
                        };
                        changeClaimStatus.mutate(changeStatusPayload, {
                            onSuccess: () => {
                                queryClient.invalidateQueries([
                                    "get-claim",
                                    { claim_id: claimIdForForm },
                                ]);
                                queryClient.invalidateQueries({
                                    queryKey: ["get-payment"],
                                });
                                toast({
                                    mode: "success",
                                    message: "Claim closed successfully",
                                });
                                onFormSubmit();
                            },
                            onError: (err) => {
                                toast({
                                    mode: "error",
                                    message:
                                        err.response?.data.message ||
                                        "Could not close claim at this time",
                                });
                                onFormSubmit();
                            },
                        });
                    }

                    if (!closeClaim) onFormSubmit();
                    queryClient.setQueryData<
                        IPaginatedApiResponse<IAddPaymentResponse>
                    >(["get-payment"], (prev) => {
                        const prevRequired =
                            prev as IPaginatedApiResponse<IAddPaymentResponse>;
                        return {
                            ...prevRequired,
                            message: response.message,
                        };
                    });

                    queryClient.invalidateQueries({
                        queryKey: [clientId, `session-note`, noteId],
                    });

                    queryClient.invalidateQueries({
                        queryKey: [
                            "invoices",
                            handleRemoveEmptyParamFromQueryParams({
                                client_id: clientId,
                            }),
                        ],
                    });

                    queryClient.invalidateQueries({
                        queryKey: [
                            "invoices",
                            handleRemoveEmptyParamFromQueryParams({
                                invoice_id: invoiceId,
                            }),
                        ],
                    });

                    queryClient.invalidateQueries({
                        queryKey: ["get-payment"],
                    });
                    queryClient.invalidateQueries({
                        queryKey: [
                            "remit-claims",
                            handleRemoveEmptyParamFromQueryParams({
                                note_id: noteId,
                            }),
                        ],
                    });

                    toast({
                        mode: "success",
                        message:
                            response.message || "Payment added successfully",
                    });
                },

                onError: (err) => {
                    if (
                        err?.response?.data.errors &&
                        err?.response?.data?.errors?.length > 0 &&
                        err?.response?.data?.errors[0].path ===
                            "payment_amount" &&
                        err?.response?.data?.errors[0].message ===
                            "must be >= 0"
                    ) {
                        toast({
                            mode: "error",
                            message: "Amount cannot be less $1",
                        });
                    } else {
                        toast({
                            mode: "error",
                            message:
                                err.response?.data.message ||
                                "Cannot add payment at this time",
                        });
                    }
                },
            });
        };
    };

    return (
        <div className="flex items-start">
            <div className="flex items-center gap-x-2 px-12">
                <ActionButton
                    size="auto"
                    variant="normal"
                    onClick={(e) => {
                        e.stopPropagation();
                        onCancelClick();
                    }}
                    className="px-4 h-7 flex items-center justify-center"
                    disabled={addPayment.isLoading}
                    actionTitle="Cancel"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="17"
                        viewBox="0 0 16 17"
                        fill="none"
                    >
                        <path
                            d="M3.3335 3.83594L12.6668 13.1693M3.3335 13.1693L12.6668 3.83594"
                            stroke={APP_COLORS.COLOR_DANGER}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </ActionButton>

                {paymentType === "write_off" ? (
                    <ActionButton
                        size="auto"
                        variant="normal"
                        className="size-5 rounded-full [border:1px_solid_var(--color-text-link)]"
                        disabled={addPayment.isLoading}
                        onClick={() =>
                            handleSubmit(handleFormSubmit("close"))()
                        }
                        actionTitle="Save and close"
                    >
                        <CheckIcon className="stroke-textLink" />
                    </ActionButton>
                ) : null}
                <ActionButton
                    size="auto"
                    variant="normal"
                    disabled={addPayment.isLoading}
                    onClick={() => handleSubmit(handleFormSubmit("hold"))()}
                    actionTitle={
                        paymentType === "write_off" ? "Save and hold" : "Save"
                    }
                >
                    <CheckIcon className="stroke-textLink size-5" />
                </ActionButton>
            </div>

            <div className="flex items-start gap-x-6 pr-6">
                <div className="w-[120px]">
                    <Controller
                        name="payment_date"
                        control={control}
                        defaultValue={handleFormatDatePickerValue(Date())}
                        render={({ field }) => (
                            <DatePicker
                                onChange={(date) => {
                                    field.onChange(date);
                                    setPaymentDate(date);
                                }}
                                selected={paymentDate}
                                hasError={!!errors.payment_date}
                                errorText={
                                    errors.payment_date?.type === "typeError"
                                        ? "invalid date value"
                                        : errors.payment_date?.message
                                }
                                className="h-7 w-[120px]"
                                popperContainer={({ children }) =>
                                    ReactDOM.createPortal(
                                        children,
                                        document.body
                                    )
                                }
                                errorTextClassName="-mt-4 text-[10px]"
                                popperProps={{
                                    strategy: "fixed",
                                    modifiers: [
                                        {
                                            name: "zIndex",
                                            options: {
                                                zIndex: 10,
                                            },
                                        },
                                    ],
                                }}
                            />
                        )}
                    />
                </div>
                <Select
                    placeholder="Source"
                    defaultValue={{
                        value: "insurance",
                        label: "Insurance",
                    }}
                    onChange={(val) => {
                        setPaymentSource((val as Options).value as string);
                        setValue("payment_type", "credit");
                    }}
                    options={paymentSourcesForSelect}
                    classNames={{
                        container: () => "w-[120px]",
                        control: () => "!h-7 !min-h-7",
                        menu: () => "!w-[130px] !z-[11]",
                        option: () => "!text-[11px]",
                        input: () => "!text-xs !h-5 !p-0 !my-0",
                        menuPortal: () => "!z-[11]",
                    }}
                    formatOptionLabel={(
                        selectData: unknown,
                        formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                    ) => {
                        return formatOptionLabelMeta.context === "menu"
                            ? (selectData as Options).label
                            : truncateString(
                                  (selectData as Options).label as string,
                                  12
                              );
                    }}
                    isLongListInDialog
                />

                <Controller
                    name="payment_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            placeholder="Payment Type"
                            options={
                                paymentSource === "interest_payment"
                                    ? paymentTypeForSelect.filter(
                                          (item) => item.value !== "write_off"
                                      )
                                    : paymentTypeForSelect
                            }
                            onChange={(val) =>
                                field.onChange((val as Options).value)
                            }
                            defaultValue={paymentTypeForSelect.find(
                                (item) => item.value === "credit"
                            )}
                            classNames={{
                                container: () => "w-[120px]",
                                control: () => "!h-7 !min-h-7",
                                menu: () => "!w-[120px]",
                                option: () => "!text-[11px]",
                                input: () => "!text-xs !h-5 !p-0 !my-0",
                            }}
                            isLongListInDialog
                        />
                    )}
                />
                {!remitId &&
                remitsForDropdown &&
                remitsForDropdown.length > 1 ? (
                    <Controller
                        name="remittance_id"
                        control={control}
                        defaultValue={claimRemitsForSelect?.[0]?.value}
                        render={({ field }) => (
                            <Select
                                placeholder="Claim CPT Code"
                                options={claimRemitsForSelect}
                                defaultValue={claimRemitsForSelect?.[0]}
                                onChange={(val) =>
                                    field.onChange((val as Options).value)
                                }
                                isDisabled={Boolean(error) || isLoading}
                                isLongListInDialog
                                className="z-[11]"
                                classNames={{
                                    container: () => "w-[120px]",
                                    control: () => "!h-7 !min-h-7",
                                    menu: () => "!w-[120px]",
                                    option: () => "!text-[11px]",
                                    input: () => "!text-xs !h-5 !p-0 !my-0",
                                }}
                            />
                        )}
                    />
                ) : null}
                {paymentType === "credit" ? (
                    <Controller
                        name="payment_method"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Payment method"
                                options={paymentMethodForSelect}
                                wrapperClass="select"
                                onChange={(val) => {
                                    field.onChange((val as Options).value);
                                }}
                                isLongListInDialog
                                hasError={!!errors.payment_method}
                                errorText={errors.payment_method?.message}
                                errorTextClassName="-mt-4 text-[10px]"
                                classNames={{
                                    container: () => "w-[140px]",
                                    control: () => "!h-7 !min-h-7",
                                    menu: () => "!w-[140px]",
                                    option: () => "!text-[11px]",
                                    input: () => "!text-xs !h-5 !p-0 !my-0",
                                }}
                            />
                        )}
                    />
                ) : null}
                {paymentMethod === "cheque" ? (
                    <Input
                        {...register("payment_info.cheque_number")}
                        placeholder="Check number"
                        className="h-7 w-[150px]"
                    />
                ) : null}
                {paymentMethod === "card" || paymentMethod === "other" ? (
                    <Input
                        {...register("auth_code")}
                        placeholder="Authorization Code"
                        hasError={!!errors.auth_code}
                        errorText={errors.auth_code?.message}
                        className="h-7 w-[150px]"
                    />
                ) : null}
                {paymentType === "write_off" ? (
                    <div className="w-[150px]">
                        <Controller
                            name="writeoff_code"
                            control={control}
                            render={({ field }) => (
                                <Select
                                    isSearchable
                                    placeholder="Write-off code"
                                    options={writeOffCodesForSelect}
                                    onChange={(val) =>
                                        field.onChange((val as Options).value)
                                    }
                                    isLongListInDialog
                                    formatOptionLabel={(data, meta) => {
                                        return meta.context === "menu"
                                            ? formatRemarkOptionLabel(
                                                  data,
                                                  meta,
                                                  getWriteOffCodeDescription,
                                                  "writeoff_code"
                                              )
                                            : truncateString(
                                                  (data as Options)
                                                      .label as string,
                                                  10
                                              );
                                    }}
                                    classNames={{
                                        container: () => "w-[150px]",
                                        control: () => "!h-7 !min-h-7",
                                        menu: () => "!w-[200px]",
                                        option: () => "!text-[11px]",
                                        input: () => "!text-xs !h-5 !p-0 !my-0",
                                    }}
                                />
                            )}
                        />
                    </div>
                ) : null}
                <div className="w-[120px]">
                    <Input
                        {...register("payment_amount")}
                        hasError={!!errors.payment_amount}
                        errorText={
                            errors.payment_amount?.type === "typeError"
                                ? "This field is required and must be a number"
                                : "Amount must be greater or equal to 0"
                        }
                        className="h-7 w-[120px]"
                        showCurrency
                        placeholder="Amount"
                        errorTextClassName="-mt-4 text-[10px]"
                    />
                </div>

                {paymentType === "refund" ? (
                    <Controller
                        name="payment_info.refund_reason"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Recoupment reason"
                                isSearchable
                                options={refundReasonForSelect}
                                onChange={(val) =>
                                    field.onChange((val as Options).value)
                                }
                                isLongListInDialog
                                classNames={{
                                    container: () => "w-[120px]",
                                    control: () => "!h-7 !min-h-7",
                                    menu: () => "!w-[120px]",
                                    option: () => "!text-[11px]",
                                    input: () => "!text-xs !h-5 !p-0 !my-0",
                                }}
                            />
                        )}
                    />
                ) : null}
                <div>
                    {fields.map((fieldItem, i) => (
                        <div
                            key={fieldItem.id}
                            className="flex items-start gap-x-2 mb-2"
                        >
                            <Controller
                                name={`remark_codes_payment_form.${i}.code`}
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        ref={(el: SelectInstance | null) => {
                                            codeSelectRefs.current[i] = el;
                                        }}
                                        placeholder="Remark code"
                                        isSearchable
                                        options={remarkCodeForSelect}
                                        value={remarkCodeForSelect.find(
                                            (code) =>
                                                field.value ===
                                                (code.value || "")
                                        )}
                                        onChange={(val) =>
                                            field.onChange(
                                                (val as Options).value
                                            )
                                        }
                                        formatOptionLabel={(
                                            selectData: unknown,
                                            formatOptionLabelMeta: FormatOptionLabelMeta<unknown>
                                        ) => {
                                            return formatOptionLabelMeta.context ===
                                                "menu"
                                                ? (selectData as Options).label
                                                : (
                                                      (selectData as Options)
                                                          .value as string
                                                  ).toUpperCase();
                                        }}
                                        isLongListInDialog
                                        classNames={{
                                            container: () => "w-[120px]",
                                            control: () => "!h-7 !min-h-7",
                                            menu: () => "!w-[120px]",
                                            option: () => "!text-[11px]",
                                            input: () =>
                                                "!text-xs !h-5 !p-0 !my-0",
                                        }}
                                        hasError={
                                            !!errors
                                                ?.remark_codes_payment_form?.[i]
                                                ?.code
                                        }
                                        errorText={
                                            errors?.remark_codes_payment_form?.[
                                                i
                                            ]?.code?.message
                                        }
                                        errorTextClassName="text-[10px] mt-4 block"
                                    />
                                )}
                            />
                            <div className="relative">
                                <Input
                                    {...register(
                                        `remark_codes_payment_form.${i}.amount`
                                    )}
                                    placeholder="Amount"
                                    hasError={
                                        !!errors?.remark_codes_payment_form?.[i]
                                            ?.amount
                                    }
                                    errorText={
                                        errors?.remark_codes_payment_form?.[i]
                                            ?.amount
                                            ? "Enter valid amount"
                                            : ""
                                    }
                                    className="h-7 w-[100px] pr-5"
                                    errorTextClassName="-mt-1 text-[10px]"
                                    showCurrency
                                />
                                {remarkAmountWarning(i) ? (
                                    <ActionButton
                                        variant="normal"
                                        size="auto"
                                        className="absolute right-1 top-[7px] z-[2]"
                                        actionTitle={
                                            remarkAmountWarning(i) as string
                                        }
                                    >
                                        <AlertTraingleIcon className="stroke-[--color-warning-text]" />
                                    </ActionButton>
                                ) : null}
                            </div>
                            <Button
                                title="Remove pair"
                                size="auto"
                                variant="normal"
                                className={
                                    !!errors?.remark_codes_payment_form?.[i]
                                        ?.code ||
                                    !!errors?.remark_codes_payment_form?.[i]
                                        ?.amount
                                        ? "self-start"
                                        : "self-center"
                                }
                                onClick={() => {
                                    remove(i);
                                    const focusIndex = i > 0 ? i - 1 : 0;
                                    setTimeout(() => {
                                        codeSelectRefs.current[
                                            focusIndex
                                        ]?.focus();
                                    }, 0);
                                }}
                                disabled={fields.length === 1}
                            >
                                <MinusCircleIcon />
                            </Button>
                        </div>
                    ))}
                    <div className="flex items-center justify-center">
                        <Button
                            size="auto"
                            variant="normal"
                            className="text-xs underline font-medium leading-none"
                            onClick={() => {
                                append(
                                    { amount: "", code: "" },
                                    {
                                        shouldFocus: false,
                                    }
                                );
                                setTimeout(() => {
                                    codeSelectRefs.current[
                                        fields.length
                                    ]?.focus();
                                }, 0);
                            }}
                        >
                            Add
                        </Button>
                    </div>
                </div>

                <TextArea
                    {...register("payment_notes")}
                    placeholder="Payment notes"
                    className="h-7 w-[200px] resize-y py-0"
                />
            </div>
        </div>
    );
}
